import { toPlain, deleteUndefined, isEqual } from '@/core/utils/object-util';

/**
 * Table model
 * @typedef {Object} TableModelState
 * @property {Object} [filters]
 * @property {Object} [order]
 * @property {number} [page]
 * @property {number} [pageSize]
 * @property {number} [defaultPageSize]
 */
/**
 * Table config
 * @typedef {Object} TableModelConfig
 * @property {string|Object} [defaultOrder]
 * @property {string} [defaultOrder.field]
 * @property {string} [defaultOrder.dir] asc/desc
 * @property {number} [defaultPageSize]
 * @property {string} [itemsField]
 * @property {function} [preprocessor]
 * @property {function} [stateInterceptor]
 */
export default class TableModel {
    /* eslint-disable no-use-before-define */
    /**
     *
     * @param {RestResource} resource
     * @param {TableModelConfig} config
     * @param {function} onLoaded
     */
    constructor(resource, config = {}, onLoaded = () => {}, onFilterChange = () => {}) {
        this.resource = resource;
        this.itemsField = config.itemsField || 'items';
        this.defaultPageSize = config.defaultPageSize || 20;
        this.setDefaultOrder(config.defaultOrder);
        this.preprocessor = config.preprocessor;
        this.stateInterceptor = config.stateInterceptor;
        this.init({});
        this.plugins = [];
        this.loading = false;
        this.failed = false;
        this.error = '';
        this.onLoaded = onLoaded;
        this.onFilterChange = onFilterChange;
    }

    setListener(listener) {
        this.listener = listener;
    }

    register(plugins) {
        const newPlugins = Array.isArray(plugins) ? plugins : [plugins];
        newPlugins.forEach((plugin) => {
            if (plugin.attach) {
                this.plugins.push(plugin);
                plugin.attach(this);
            }
        });
    }

    restore(load = true) {
        this.plugins.forEach(plugin => plugin.restore());
        if (!load) {
            return Promise.resolve([]);
        }
        return this.load();
    }

    /**
     *
     * @param state
     */
    init(state) {
        this.total = 0;
        this.items = [];
        this.setState(state);

        if (Object.keys(state.filters || {}).length) {
            this.onFilterChange(state.filters);
        }
    }

    /**
     *
     * @param {TableModelState} state
     */
    setState(state) {
        this.filters = state.filters || {};
        this.page = state.page || 1;
        if (this.page < 0) {
            this.page = 1;
        }
        if (state.pageSize && state.pageSize > 0) {
            this.pageSize = state.pageSize;
        } else if (!this.pageSize) {
            this.pageSize = this.defaultPageSize;
        }
        if (state.order && state.order.field) {
            this.order = state.order;
        } else if (this.defaultOrder) {
            this.order = this.defaultOrder;
        }
    }

    getState() {
        return {
            page: this.page,
            pageSize: this.pageSize,
            filters: this.filters ? toPlain(this.filters) : {},
            order: this.order ? toPlain(this.order) : {},
        };
    }

    getQuery() {
        return Object.assign({}, this.filters, {
            page: this.page,
            pageSize: this.pageSize,
            sortBy: prepareOrderQuery(this.order) || undefined,
        });
    }

    async load() {
        const query = this.getQuery();
        this.loading = true;
        const started = Date.now();
        try {
            this.failed = false;
            const response = await this.resource.find(query);
            const items = response[this.itemsField];
            this.items = this.preprocessor ? this.preprocessor(items, response) : items;
            this.total = response.totalCount;
            this.loadedAt = Date.now();
            this.onLoaded(response);
        } catch (e) {
            this.loading = false;
            this.failed = true;
            this.error = e;
        }
        const minSpinner = 300;
        const duration = (Date.now() - started);
        if (duration < minSpinner) {
            setTimeout(() => {
                this.loading = false;
            }, minSpinner - duration);
        } else {
            this.loading = false;
        }
    }

    isStale(seconds = 60) {
        return (!this.loadedAt || Date.now() > (this.loadedAt + seconds * 1000));
    }

    removeRow(row) {
        const idx = this.items.indexOf(row);
        if (idx === -1) {
            return;
        }
        this.items.splice(idx, 1);
        if (this.total) {
            this.total -= 1;
        } else if (this.page > 1) {
            this.goToPrevious();
        }
    }

    getFilters() {
        return toPlain(this.filters);
    }

    isSameFilter(field, val) {
        const value = (!val && val !== 0 && val !== false) ? undefined : val;
        return isEqual(this.filters[field], value);
    }

    filter(criteria) {
        const filters = Object.assign({}, this.filters, criteria);
        deleteUndefined(filters);
        this.onFilterChange(filters);
        return this.update({ filters });
    }

    updateFilters(criteria) {
        const filters = Object.assign({}, this.filters, criteria);
        deleteUndefined(filters);
        this.onFilterChange(filters);
        return this.updateNoListener({ filters });
    }

    sort(field, direction = 'asc') {
        return this.update({ order: { field, dir: direction } });
    }

    getSort() {
        return this.order;
    }

    setDefaultOrder(defaultOrder) {
        if (defaultOrder) {
            if (defaultOrder instanceof Object) {
                this.defaultOrder = defaultOrder;
            } else {
                this.defaultOrder = this.parseOrder(defaultOrder);
            }
        }
    }

    // eslint-disable-next-line class-methods-use-this
    parseOrder(order) {
        if (!order) {
            return {};
        }
        if (order[0] === '-') {
            return { field: order.substr(1), dir: 'desc' };
        }
        return { field: order, dir: 'asc' };
    }

    update(diff) {
        const current = this.getState();
        if (!diff.page && this.page !== 1) {
            // eslint-disable-next-line no-param-reassign
            diff.page = 1;
        }
        const newState = Object.assign(current, diff);
        this.setState(newState);
        if (typeof this.listener === 'function') {
            this.listener(current, newState, diff);
        }
    }

    updateNoListener(diff) {
        const current = this.getState();
        if (!diff.page && this.page !== 1) {
            // eslint-disable-next-line no-param-reassign
            diff.page = 1;
        }
        const newState = Object.assign(current, diff);
        this.setState(newState);
    }

    reset() {
        this.setState({ page: 1 });
        this.update({ page: 1 });
    }

    fetch(page, pageSize) {
        this.paging(page, pageSize);
        return this.load();
    }

    paging(page, pageSize) {
        this.update({ page, pageSize });
    }

    goToPage(page) {
        if (page <= 0) {
            return Promise.resolve();
        }
        return this.update({ page });
    }

    goToNext() {
        return this.goToPage(this.page + 1);
    }

    goToPrevious() {
        return this.goToPage(this.page - 1);
    }

    hasItems() {
        return this.items && !!this.items.length;
    }

    hasPages() {
        return this.total > 10;
    }
}

function prepareOrderQuery(order) {
    if (!order || !order.field) {
        return '';
    }
    return `${order.dir === 'desc' ? '-' : ''}${order.field}`;
}
